import {Button} from '@orbit-ui/components/button';
import {Loader} from '@orbit-ui/components/loader';
import {Table, TableHeader, TableBody, TableRow, TableHead} from '@orbit-ui/components/table';
import {useState} from 'react';
import {useUserEventsQuery} from '../api/events-hooks';
import {EventsQueryParameters, WorkOSEvent} from '../types/events';
import {EventItem} from './event-item';

type EventsListProps = {
  initialParameters?: EventsQueryParameters;
};

/**
 * Component to display a list of events with pagination
 */
export function EventsList({initialParameters = {}}: EventsListProps) {
  const [queryParameters, setQueryParameters] = useState<EventsQueryParameters>({
    limit: 50,
    offset: 0,
    ...initialParameters,
  });

  const {data, isLoading, isError, error} = useUserEventsQuery(queryParameters);

  // Handle load more button click
  const handleLoadMore = () => {
    if (!data) return;

    const newOffset = (queryParameters.offset ?? 0) + (queryParameters.limit ?? 50);

    setQueryParameters({
      ...queryParameters,
      offset: newOffset,
    });
  };

  if (isLoading && !data) {
    return <Loader />;
  }

  if (isError) {
    return (
      <div className="p-4 text-red-500">
        Error loading events: {error instanceof Error ? error.message : 'Unknown error'}
      </div>
    );
  }

  // Define a type for the raw API response
  type RawWorkOSEvent = {
    id: string;
    event_type: string;
    organization_id?: string;
    user_id?: string;
    data: {
      String?: string;
      Valid?: boolean;
      [key: string]: unknown;
    };
    occurred_at: string;
    synced_at: string;
  };

  // Check if data exists and is an array (direct API response) or if it has a data property (wrapped response)
  const rawEvents: RawWorkOSEvent[] | undefined = Array.isArray(data)
    ? (data as RawWorkOSEvent[])
    : data?.data;

  // Transform the events to match our expected WorkOSEvent structure
  const events: WorkOSEvent[] | undefined = rawEvents?.map((event: RawWorkOSEvent) => {
    // Parse the String property if it exists and is a string
    let parsedData: Record<string, unknown> = {...event.data};

    if (event.data?.String && typeof event.data.String === 'string') {
      try {
        const parsed = JSON.parse(event.data.String) as Record<string, unknown>;

        parsedData = {
          ...parsed,
          // Create user object from parsed data
          user:
            typeof parsed.user_id === 'string'
              ? {
                  id: parsed.user_id,
                  email: typeof parsed.email === 'string' ? parsed.email : undefined,
                }
              : undefined,
          // Create connection object from parsed data
          connection:
            typeof parsed.type === 'string'
              ? {
                  id: parsed.type,
                  type: parsed.type,
                }
              : undefined,
        };
      } catch (error_) {
        console.error('Failed to parse event data:', error_);
      }
    }

    return {
      ...event,
      data: parsedData,
    } as WorkOSEvent;
  });

  if (!events?.length) {
    return <div className="p-4 text-gray-500">No events found</div>;
  }

  return (
    <div className="rounded border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Event Type</TableHead>
            <TableHead>User</TableHead>
            <TableHead>Connection</TableHead>
            <TableHead>Event age</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>{events?.map((event) => <EventItem key={event.id} event={event} />)}</TableBody>
      </Table>

      {/* Handle pagination for both response formats */}
      {rawEvents && rawEvents.length >= (queryParameters.limit ?? 50) && (
        <div className="mt-4 flex justify-center">
          <Button onClick={handleLoadMore} disabled={isLoading}>
            {isLoading ? 'Loading...' : 'Load More'}
          </Button>
        </div>
      )}
    </div>
  );
}
